import React, {Component} from 'react';
import PropTypes from 'prop-types';
import Panel from './panel';
import * as SharedStyle from '../../shared-style';
import {FormLabel, FormSelect, FormNumberInput} from '../style/export';
import PropertyStyle from '../../catalog/properties/shared-property-style';
import {UNITS_MONEY, UNITS_LENGTH, ROOM_TYPES, KITCHEN_LAYOUTS} from '../../constants';
import BubbleChartModal from '../bubble-chart-modal';
import {fromJS} from 'immutable';
import {ApiUtils} from '../../utils/export';

const unitContainerStyle = {width: '5em'};

const contentArea = {
  height: 'auto',
  overflowY: 'auto',
  padding: '0.25em 1.15em',
  cursor: 'pointer',
  marginBottom: '1em',
  userSelect: 'none'
};

const countButtonStyle = {
  width: '24px',
  height: '24px',
  border: '1px solid #CCC',
  borderRadius: '4px',
  background: 'white',
  cursor: 'pointer'
};

const categoryDividerStyle = {
  paddingBottom: '0.5em',
  borderBottom: '1px solid #888',
};

const buttonStyle = {
  padding: '10px 15px',
  backgroundColor: SharedStyle.SECONDARY_COLOR.main,
  color: SharedStyle.COLORS.white,
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 'bold',
  margin: '0 5px'
};

// 旋转加载图标样式
const spinnerStyle = {
  display: 'inline-block',
  width: '36px',
  height: '36px',
  border: '5px solid rgba(255, 255, 255, 0.3)',
  borderRadius: '50%',
  borderTopColor: SharedStyle.COLORS.white,
  animation: 'spin 1s linear infinite',
  flexShrink: 0
};

// 背景蒙版样式（模糊 + 阻止交互）
const backdropStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.3)',
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)', // Safari 支持
  zIndex: 9999,
  pointerEvents: 'auto', // 阻止所有交互
  cursor: 'default' // 保持默认鼠标样式
};

// 提示信息容器样式
const designingMessageStyle = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: 'rgba(0, 0, 0, 0.85)',
  color: SharedStyle.COLORS.white,
  padding: '30px 45px',
  borderRadius: '8px',
  zIndex: 10000,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '23px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
  minWidth: '300px',
  textAlign: 'center',
  pointerEvents: 'auto'
};

// 添加旋转动画的样式标签（将在组件中动态添加）
const spinnerKeyframes = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;

export default class PanelAIGenerateCondition extends Component {

  constructor(props, context) {
    super(props, context);

    this.state = {
      // the aiDesignParams is defined in the redux state, src/models.js
      aiDesignParams: this.props.state.aiDesignParams,
      showBubbleChart: false,
      bubbleChartDrawingMode: 'move',
      roomRelationships: null,
      isLoading: false,
      showDesigningMessage: false
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    return this.state.aiDesignParams !== nextState.aiDesignParams ||
           this.state.showBubbleChart !== nextState.showBubbleChart ||
           this.state.bubbleChartDrawingMode !== nextState.bubbleChartDrawingMode ||
           this.state.isLoading !== nextState.isLoading ||
           this.state.showDesigningMessage !== nextState.showDesigningMessage;
  }

  componentDidUpdate(prevProps, prevState) {
    // Check if aiDesignParams has changed
    if (prevState.aiDesignParams !== this.state.aiDesignParams) {
      // Dispatch the action to update Redux state
      this.context.aiDesignActions.updateParams(this.state.aiDesignParams.toJS()); // Convert Immutable Map to plain JS object
    }
  }

  // Generic handler for aiDesignParams changes
  handleParamChange = (paramName, value) => {
    this.setState(prevState => ({
      aiDesignParams: prevState.aiDesignParams.set(paramName, value)
    }));
  }

  // Event wrapper for form elements
  handleInputChange = (paramName) => (event) => {
    this.handleParamChange(paramName, event.target.value);
  }

  // Bubble chart handlers
  openBubbleChart = () => {
    this.setState({ showBubbleChart: true });
  }

  closeBubbleChart = () => {
    this.setState({ showBubbleChart: false });
  }

  changeBubbleChartDrawingMode = (mode) => {
    this.setState({ bubbleChartDrawingMode: mode });
  }

  saveRoomRelationships = (data) => {
    // Save the room relationships data
    this.setState(prevState => ({
      roomRelationships: data,
      aiDesignParams: prevState.aiDesignParams.set('roomRelationships', 
        prevState.aiDesignParams.get('roomRelationships')
          .set('bubbles', fromJS(data.bubbles))
          .set('connections', fromJS(data.connections))
      )
    }));
  }

  // FloorPlan AI Design API handler
  handleFloorPlanAIDesign = async () => {
    const { state } = this.props;
    const { translator, projectActions, aiDesignActions } = this.context;
    
    this.setState({ isLoading: true, showDesigningMessage: true });
    
    try {
      // Call the API utility function
      await ApiUtils.callFloorplanAiDesignApi(
        state,
        // onSuccess callback
        (floorplanData) => {
          projectActions.loadProject(floorplanData.scene);
          if (floorplanData.aiDesignParams) {
            aiDesignActions.updateParams(floorplanData.aiDesignParams);
          }
          this.setState({ showDesigningMessage: false });
          alert(translator.t('FloorPlan AI design applied successfully'));
        },
        // onError callback
        (error) => {
          this.setState({ showDesigningMessage: false });
          alert(translator.t('Error generating FloorPlan: ') + error.message);
        }
      );
    } catch (error) {
      // 捕获任何未处理的错误
      this.setState({ showDesigningMessage: false });
    } finally {
      this.setState({ isLoading: false });
    }
  }

  componentDidMount() {
    // 添加旋转动画样式到页面
    if (!document.getElementById('spinner-keyframes-style')) {
      const style = document.createElement('style');
      style.id = 'spinner-keyframes-style';
      style.textContent = spinnerKeyframes;
      document.head.appendChild(style);
    }
  }

  render() {
    let { translator } = this.context;
    const { showBubbleChart, bubbleChartDrawingMode, aiDesignParams, isLoading, showDesigningMessage } = this.state;

    return (
      <Panel name={this.context.translator.t('AI Generate Condition')}>
        <div style={contentArea} onWheel={e => e.stopPropagation()}>
          
          {/* 设计中的提示信息 */}
          {showDesigningMessage && (
            <>
              {/* 背景模糊蒙版 */}
              <div style={backdropStyle}></div>
              {/* 提示信息 */}
              <div style={designingMessageStyle}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '23px' }}>
                  <div style={spinnerStyle}></div>
                  <span style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: '1.5' }}>
                    建筑平面智能设计中，预计10分钟完成设计，请勿关闭当前界面。
                  </span>
                </div>
              </div>
            </>
          )}

          <table style={PropertyStyle.tableStyle}>
            <tbody>
            <tr>
              <td style={PropertyStyle.firstTdStyle}><FormLabel>{translator.t('Length Unit')}</FormLabel></td>
              <td>
                <FormSelect value={this.state.aiDesignParams.lengthUnit} onChange={this.handleInputChange('lengthUnit')}>
                  {UNITS_LENGTH.map(unit => (
                    <option key={unit} value={unit}>{unit}</option>
                  ))}
                </FormSelect>
              </td>
            </tr>

            <tr>
              <td style={PropertyStyle.firstTdStyle}><FormLabel>{translator.t('Budget')}</FormLabel></td>
              <td>
                <FormNumberInput value={this.state.aiDesignParams.budget || ''} onChange={this.handleInputChange('budget')} />
              </td>
              <td style={unitContainerStyle}>
                <FormSelect value={this.state.aiDesignParams.budgetUnit} onChange={this.handleInputChange('budgetUnit')}>
                  {UNITS_MONEY.map(unit => (
                    <option key={unit} value={unit}>{unit}</option>
                  ))}
                </FormSelect>
              </td>
            </tr>
            
            <tr>
              <td style={PropertyStyle.firstTdStyle}><FormLabel>{translator.t('Kitchen Layout')}</FormLabel></td>
              <td>
                <FormSelect value={this.state.aiDesignParams.kitchenLayout} onChange={this.handleInputChange('kitchenLayout')}>
                  {KITCHEN_LAYOUTS.map(layout => (
                    <option key={layout} value={layout}>{translator.t(layout)}</option>
                  ))}
                </FormSelect>
              </td>
            </tr>
            
            <tr>
              <td colSpan="3" style={categoryDividerStyle}><FormLabel>{translator.t('Number of rooms')}</FormLabel></td>
            </tr>
            {Array.from(aiDesignParams.roomCounts.entries()).map(([roomType, count]) => {
              // convert the room type to the state key
              return (
                <tr key={roomType}>
                  <td style={PropertyStyle.firstTdStyle}><FormLabel>{translator.t(roomType)}</FormLabel></td>
                  <td style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button 
                      onClick={() => this.setState(prevState => ({
                        aiDesignParams: prevState.aiDesignParams.set(
                          'roomCounts',
                          prevState.aiDesignParams.get('roomCounts').set(roomType, Math.max(0, parseInt(count) - 1))
                        )
                      }))
                      }
                      style={countButtonStyle}
                    >
                      -
                    </button>
                    <FormNumberInput 
                      value={count || 0}
                      onChange={event => this.setState(prevState => ({
                        aiDesignParams: prevState.aiDesignParams.set(
                          'roomCounts',
                          prevState.aiDesignParams.get('roomCounts').set(roomType, Math.max(0, parseInt(event.target.value) || 0))
                        )
                      }))
                      }
                      min={0}
                      max={100}
                      style={{ width: '50px', textAlign: 'center' }}
                    />
                    <button   
                      onClick={() => this.setState(prevState => ({
                        aiDesignParams: prevState.aiDesignParams.set(
                          'roomCounts',
                          prevState.aiDesignParams.get('roomCounts').set(roomType, Math.min(100, parseInt(count) + 1))
                        )
                      }))
                      }
                      style={countButtonStyle}
                    >
                      +
                    </button>
                  </td>
                </tr>
              );
            })}
            <tr>
              <td colSpan="3" style={{ textAlign: 'center', paddingTop: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                  <button 
                    onClick={this.openBubbleChart}
                    style={buttonStyle}
                  >
                    {translator.t('Edit Room Relationships')}
                  </button>
                  <button 
                    onClick={this.handleFloorPlanAIDesign}
                    style={buttonStyle}
                    disabled={isLoading}
                  >
                    {isLoading 
                      ? translator.t('Processing...') 
                      : translator.t('FloorPlan AI Design')}
                  </button>
                </div>
              </td>
            </tr>
            </tbody>
          </table>

          {/* Bubble Chart Modal for editing the room relationships */}
          <BubbleChartModal 
            show={showBubbleChart}
            onClose={this.closeBubbleChart}
            onSave={this.saveRoomRelationships}
            onChangeDrawingMode={this.changeBubbleChartDrawingMode}
            drawingMode={bubbleChartDrawingMode}
            roomCounts={this.state.aiDesignParams.roomCounts.toJS()}
            translator={translator}
            existingRelationships={this.state.aiDesignParams.roomRelationships.toJS()}
          />
        </div>
      </Panel>
    );
  }

}

PanelAIGenerateCondition.propTypes = {
  state: PropTypes.object.isRequired,
};

PanelAIGenerateCondition.contextTypes = {
  catalog: PropTypes.object.isRequired,
  translator: PropTypes.object.isRequired,
  aiDesignActions: PropTypes.object.isRequired,
  projectActions: PropTypes.object.isRequired,
};
